import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker, ZoomControl, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet's default icon issues
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faHotel,
  faCalendarAlt,
  faSave,
  faChevronDown,
  faSpinner,
  faTimes,
  faFilter,
  faUser,
  faUsers,
  faGlobe,
  faLock,
  faBuilding,
  faCalendarCheck,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faClock,
  faDirections,
  faCheckCircle,
  faList,
  faLayerGroup,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  startAfter,
  orderBy,
  Timestamp,
  documentId,
  doc,
  getDoc
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { Country, State, City } from 'country-state-city';
import styles from './RoutesMap.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultVenuePhoto from '../../assets/Images/defaultvenuepicture.png';
import RightMenu from '../RightMenu';
import LeftMenu from '../LeftMenu';
import moment from 'moment';

// Fix Leaflet's default icon issues
let DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;
const MapContext = React.createContext(null);

// Map Updater component to handle map updates without re-rendering
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
};

// Map Themes - Updated to include POI with OpenStreetMap
const mapThemes = {
  // Updated to OpenStreetMap with POIs instead of satellite imagery
  light: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  dark: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
};

// Cache management
const CACHE_DURATION = 3 * 60 * 60 * 1000; // 3 hours
const mapCache = new Map();

const getCacheKey = (type, params) => {
  return `${type}_${JSON.stringify(params)}`;
};

const getCachedData = (key) => {
  const cached = mapCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  mapCache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  mapCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Helper function to chunk arrays for batch queries
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// Custom Marker Component
const UserMarker = ({ user, onClick, isSelected }) => {
  const position = [user.venue.latitude, user.venue.longitude];
  
  return (
    <CircleMarker 
      center={position}
      radius={8}
      pathOptions={{
        fillColor: isSelected ? '#E2384D' : '#1a73e8',
        fillOpacity: 0.8,
        weight: 2,
        color: 'white'
      }}
      eventHandlers={{
        // Directly pass the complete user object to preserve ALL data
        click: () => onClick(user)
      }}
    >
      <Popup>
        <div className={styles.popupContent}>
          <img 
            src={user.profilePictureUrl || defaultProfilePhoto} 
            alt={user.fullName}
            className={styles.popupImage}
          />
          <div className={styles.popupInfo}>
            <h3>{user.userName}</h3>
            <p>{user.userSubType} • {user.venue.displayName}</p>
          </div>
        </div>
      </Popup>
    </CircleMarker>
  );
};

// Event and Hotel markers - Enhanced with better visuals
const VenueMarker = ({ venue, type, onClick }) => {
  const position = [venue.latitude, venue.longitude];
  const color = type === 'event' ? '#E2384D' : '#4caf50';
  const icon = type === 'event' ? faBuilding : faHotel;
  
  // Ensure the venue ID is passed from the source data
  const venueWithId = {
    ...venue,
    // If venue.id exists, use it, otherwise try to extract from venueDetails
    id: venue.id || (venue.venueDetails?.venueData?.id) || null
  };
  
  return (
    <CircleMarker 
      center={position}
      radius={10}
      pathOptions={{
        fillColor: color,
        fillOpacity: 0.8,
        weight: 2,
        color: 'white'
      }}
      eventHandlers={{
        click: () => onClick({ venue: venueWithId, type })
      }}
    >
      <Popup>
        <div className={styles.popupContent}>
          <div className={styles.popupHeader}>
            <FontAwesomeIcon icon={icon} className={styles.venueIcon} /> 
            <span>{type === 'event' ? 'Event Venue' : 'Hotel'}</span>
          </div>
          <h3>{venue.displayName}</h3>
          <p>{venue.formattedAddress}</p>
        </div>
      </Popup>
    </CircleMarker>
  );
};

// Schedule Location Marker Component
const ScheduleMarker = ({ location, type, onClick, isActive = false }) => {
  if (!location || !location.latitude || !location.longitude) return null;
  
  // Check if location coordinates are valid numbers
  const lat = parseFloat(location.latitude);
  const lng = parseFloat(location.longitude);
  
  if (isNaN(lat) || isNaN(lng)) return null;
  
  // Define marker style
  const markerStyle = {
    fillColor: '#1a73e8', // Blue for schedule markers
    color: 'white',
    fillOpacity: 0.8,
    weight: 2,
    radius: isActive ? 10 : 8
  };
  
  return (
    <CircleMarker
      center={[lat, lng]}
      pathOptions={markerStyle}
      eventHandlers={{
        click: () => onClick({...location, type})
      }}
    >
      <Popup>
        <div className={styles.markerPopup}>
          <h3>{location.displayName}</h3>
          <p>{location.formattedAddress}</p>
          <div className={styles.popupType}>
            {type === 'from' ? 'From Location' : 'To Location'}
          </div>
        </div>
      </Popup>
    </CircleMarker>
  );
};

// Map Legend component to show icons for venues and hotels
const MapLegend = ({ showLegend, setShowLegend, showSchedule }) => {
  if (!showLegend) return null;
  
  return (
    <div className={styles.mapLegend}>
      <div className={styles.legendHeader}>
        <h3>Markers</h3>
        <button 
          className={styles.legendCloseButton}
          onClick={() => setShowLegend(false)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      
      {showSchedule && (
        <div className={styles.legendItem}>
          <div className={styles.legendMarker} style={{ backgroundColor: '#1a73e8' }}></div>
          <span>Schedule Locations</span>
        </div>
      )}
      
      <div className={styles.legendItem}>
        <div className={styles.legendMarker} style={{ backgroundColor: '#E2384D', border: '2px solid white', width: '14px', height: '14px' }}></div>
        <span>Event Venue</span>
      </div>
      
      <div className={styles.legendItem}>
        <div className={styles.legendMarker} style={{ backgroundColor: '#4caf50', border: '2px solid white', width: '14px', height: '14px' }}></div>
        <span>Hotel</span>
      </div>
      
      {!showSchedule && (
        <div className={styles.legendItem}>
          <div className={styles.legendMarker} style={{ backgroundColor: '#1a73e8' }}></div>
          <span>Users</span>
        </div>
      )}
    </div>
  );
};

// Date Picker Modal Component - Enhanced with venue information and schedule tab restrictions
const DatePickerModal = ({ isOpen, onClose, onSelectDate, selectedDate, allowedDates, isElite, selectedEvent, activeTab }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  if (!isOpen) return null;

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const isDateAllowed = (date) => {
    // For schedule tab, only allow selection of tour dates
    if (activeTab === 'schedule') {
      if (!selectedEvent || !selectedEvent.eventDates) return false;
      
      // Convert to YYYY-MM-DD format for comparison
      const dateString = date.toISOString().split('T')[0];
      return selectedEvent.eventDates.some(td => td.date === dateString);
    }
    
    // For other tabs, follow normal rules
    if (isElite) return true;
    
    const dateString = date.toISOString().split('T')[0];
    return allowedDates.includes(dateString);
  };

  const getVenueForDate = (date) => {
    if (!selectedEvent || !selectedEvent.eventDates) return null;
    
    // Use YYYY-MM-DD format for consistency
    const dateString = date.toISOString().split('T')[0];
    // Find the exact tour date match
    const eventDate = selectedEvent.eventDates.find(td => td.date === dateString);
    
    if (eventDate && eventDate.event && eventDate.event.venueDetails && eventDate.event.venueDetails.venueData) {
      return eventDate.event.venueDetails.venueData.displayName;
    }
    
    return null;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarEmptyDay}></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Create date with noon time to avoid timezone issues
      const date = new Date(selectedYear, selectedMonth, day, 12, 0, 0);
      const isAllowed = isDateAllowed(date);
      const isSelected = selectedDate && 
        date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0];
      
      const venueInfo = getVenueForDate(date);
      
      days.push(
        <div
          key={day}
          className={`${styles.calendarDay} 
            ${!isAllowed ? styles.disabled : ''} 
            ${isSelected ? styles.selected : ''}
            ${venueInfo ? styles.haseventDate : ''}`}
          onClick={() => {
            if (isAllowed) {
              onSelectDate(date);
              onClose();
            }
          }}
        >
          <div className={styles.calendarDayContent}>
            <span className={styles.dayNumber}>{day}</span>
            {venueInfo && (
              <span className={styles.calendarVenueInfo} title={venueInfo}>
                {venueInfo}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.datePickerModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Select Date</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.monthYearSelector}>
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className={styles.monthSelect}
          >
            {['January', 'February', 'March', 'April', 'May', 'June', 
              'July', 'August', 'September', 'October', 'November', 'December']
              .map((month, index) => (
                <option key={month} value={index}>{month}</option>
              ))}
          </select>
          
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className={styles.yearSelect}
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <div className={styles.calendarGrid}>
          <div className={styles.calendarHeader}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={styles.calendarHeaderDay}>{day}</div>
            ))}
          </div>
          <div className={styles.calendarDaysContainer}>
            <div className={styles.calendarDays}>
              {renderCalendar()}
            </div>
          </div>
        </div>
        
        {!isElite && activeTab !== 'schedule' && (
          <div className={styles.calendarNote}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Free users can only select their assigned tour dates. <a href="/membership">Upgrade</a> to access all dates.</span>
          </div>
        )}
        
        {activeTab === 'schedule' && (
          <div className={styles.calendarNote}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Only tour dates can be selected in Schedule view.</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Upgrade Modal Component
const UpgradeModal = ({ isOpen, onClose, feature }) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.upgradeModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Upgrade to Elite</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.modalContent}>
          <p>
            {feature} is available exclusively to Elite members.
            Upgrade now to unlock this and other premium features.
          </p>
          
          <div className={styles.upgradeFeatures}>
            <h4>Elite Benefits:</h4>
            <ul>
              <li>View any date on the map</li>
              <li>Access global user locations</li>
              <li>Advanced filtering options</li>
              <li>Priority support</li>
            </ul>
          </div>
        </div>
        
        <div className={styles.modalActions}>
          <button onClick={onClose} className={styles.secondaryButton}>
            Maybe Later
          </button>
          <button 
            onClick={() => navigate('/membership')} 
            className={styles.primaryButton}
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Venue Card Component - Updated to handle both types of cards closing together
const VenueCard = ({ venue, users, onViewSchedule, onViewVenue, onViewProfile, onClose }) => {
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  
  if (!venue) return null;
  
  // Determine which data structure we have (Schedule vs Connections/Global)
  // If this is a user from Connections/Global, it will have these properties
  const isUserFromConnections = venue.userId && venue.venue;
  
  // Get the correct venue object for display
  const displayVenue = isUserFromConnections ? venue.venue : venue;
  
  return (
    <div className={styles.venueCard}>
      <button 
        className={styles.venueCardClose} 
        onClick={onClose}
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      
      <div className={styles.venueCardContent}>
        <div className={styles.venueImage}>
          <img 
            src={displayVenue.photoUrl || displayVenue.placeCoverPhotoUrl || defaultVenuePhoto}
            alt={displayVenue.displayName}
          />
        </div>
        
        <div className={styles.venueInfo}>
          <h3>{displayVenue.displayName}</h3>
          <p className={styles.venueAddress}>{displayVenue.formattedAddress}</p>
          
          <div className={styles.venueActions}>
            {onViewSchedule && (
              <button onClick={onViewSchedule} className={styles.actionButton}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                View Schedule
              </button>
            )}
            <button 
              onClick={() => onViewVenue(isUserFromConnections ? venue.venue : venue)}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faBuilding} />
              Venue Details
            </button>
          </div>
        </div>
      </div>
      
      {users && users.length > 0 && (
        <div className={styles.usersList}>
          <div className={styles.usersScroll}>
            {users.map((user, index) => (
              <div
                key={user.userId || index}
                className={`${styles.userItem} ${index === selectedUserIndex ? styles.selected : ''}`}
                onClick={() => setSelectedUserIndex(index)}
              >
                <img
                  src={user.profilePictureUrl || defaultProfilePhoto}
                  alt={user.fullName}
                  className={styles.userPhoto}
                />
                <span className={styles.userName}>{user.userName}</span>
                <span className={styles.userName}>{user.userSubType}</span>
              </div>
            ))}
            {users && users.length > 0 && (
              <button onClick={() => onViewProfile(users[selectedUserIndex])} className={styles.actionButton}>
                <FontAwesomeIcon icon={faUser} />
                View Profile
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Location Filter Component - Updated with Mumbai default
const LocationFilter = ({ onLocationSelect, disabled }) => {
  // Pre-select India, Maharashtra, Mumbai
  const [selectedCountry, setSelectedCountry] = useState({
    isoCode: 'IN',
    name: 'India'
  });
  const [selectedState, setSelectedState] = useState({
    isoCode: 'MH',
    name: 'Maharashtra'
  });
  const [selectedCity, setSelectedCity] = useState({
    name: 'Mumbai',
    latitude: '19.0760',
    longitude: '72.8777'
  });
  
  // Initialize with the correct available options
  const [availableStates, setAvailableStates] = useState(
    State.getStatesOfCountry('IN') || []
  );
  const [availableCities, setAvailableCities] = useState(
    City.getCitiesOfState('IN', 'MH') || []
  );

  // Initialize location on component mount
  useEffect(() => {
    if (selectedCountry && selectedState && selectedCity) {
      onLocationSelect({
        country: selectedCountry.name,
        state: selectedState.name,
        city: selectedCity.name,
        latitude: parseFloat(selectedCity.latitude),
        longitude: parseFloat(selectedCity.longitude)
      });
    }
  }, []);

  const handleCountryChange = (countryCode) => {
    const country = Country.getCountryByCode(countryCode);
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    
    if (country) {
      const states = State.getStatesOfCountry(countryCode);
      setAvailableStates(states);
      setAvailableCities([]);
    }
  };

  const handleStateChange = (stateCode) => {
    if (!selectedCountry) return;
    
    const state = State.getStateByCodeAndCountry(stateCode, selectedCountry.isoCode);
    setSelectedState(state);
    setSelectedCity(null);
    
    if (state) {
      const cities = City.getCitiesOfState(selectedCountry.isoCode, stateCode);
      setAvailableCities(cities);
    }
  };

  const handleCityChange = (cityName) => {
    const city = availableCities.find(c => c.name === cityName);
    setSelectedCity(city);
    
    if (city && selectedCountry && selectedState) {
      onLocationSelect({
        country: selectedCountry.name,
        state: selectedState.name,
        city: city.name,
        latitude: parseFloat(city.latitude),
        longitude: parseFloat(city.longitude)
      });
    }
  };

  return (
    <div className={styles.locationFilter}>
      <select
        value={selectedCountry?.isoCode || ''}
        onChange={(e) => handleCountryChange(e.target.value)}
        className={styles.locationSelect}
        disabled={disabled}
      >
        <option value="">Select Country</option>
        {Country.getAllCountries().map(country => (
          <option key={country.isoCode} value={country.isoCode}>
            {country.flag} {country.name}
          </option>
        ))}
      </select>

      <select
        value={selectedState?.isoCode || ''}
        onChange={(e) => handleStateChange(e.target.value)}
        className={styles.locationSelect}
        disabled={!selectedCountry || disabled}
      >
        <option value="">Select State</option>
        {availableStates.map(state => (
          <option key={state.isoCode} value={state.isoCode}>
            {state.name}
          </option>
        ))}
      </select>

      <select
        value={selectedCity?.name || ''}
        onChange={(e) => handleCityChange(e.target.value)}
        className={styles.locationSelect}
        disabled={!selectedState || disabled}
      >
        <option value="">Select City</option>
        {availableCities.map(city => (
          <option key={city.name} value={city.name}>
            {city.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// User Type Filter Component
const UserTypeFilter = ({ value, onChange, disabled }) => {
  return (
    <div className={styles.userTypeFilter}>
      <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.userTypeSelect}
        disabled={disabled}
      >
        <option value="Artist">Artist</option>
        <option value="Crew">Crew</option>
        <option value="Manager">Manager</option>
        <option value="Vendor">Vendor</option>
        <option value="Staff">Staff</option>
      </select>
    </div>
  );
};

// No Data Messaging Component
const NoDataMessage = ({ activeTab, userData }) => {
  const messages = {
    schedule: "No schedule data available for this date. Please add tour dates to see schedules.",
    connections: userData?.membershipTier === 'Free' 
      ? "Free users can only view connections on their assigned tour dates. Upgrade to see connections on any date."
      : "No connections found for this date.",
    global: userData?.membershipTier === 'Free'
      ? "Free users cannot view global data. Please upgrade to access the global view."
      : "No users found at this location for the selected date."
  };

  return (
    <div className={styles.emptyStateOverlay}>
      <div className={styles.emptyStateContent}>
        <FontAwesomeIcon 
          icon={activeTab === 'schedule' ? faCalendarAlt : (activeTab === 'connections' ? faUsers : faGlobe)} 
          size="3x" 
        />
        <h3>{activeTab === 'schedule' ? 'No Schedule Data' : (activeTab === 'connections' ? 'No Connections' : 'No Users Found')}</h3>
        <p>{messages[activeTab]}</p>
        {userData?.membershipTier === 'Free' && activeTab !== 'schedule' && (
          <a href="/membership" className={styles.upgradeLink}>
            Upgrade Now
          </a>
        )}
      </div>
    </div>
  );
};

// Snapchat-style compact user list component
const CompactUserList = ({ users, selectedUserId, onUserSelect, onLoadMore, hasMore, loading }) => {
  return (
    <div className={styles.compactUserList}>
      <div className={styles.compactUserScroll}>
        {users.map((user) => (
          <div
            key={user.id}
            className={`${styles.compactUserItem} ${selectedUserId === user.id ? styles.selected : ''}`}
            onClick={() => onUserSelect(user)}
          >
            <div className={styles.compactUserPhoto}>
              <img
                src={user.profilePictureUrl || defaultProfilePhoto}
                alt={user.fullName}
              />
              {selectedUserId === user.id && (
                <div className={styles.selectedIndicator}>
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
            </div>
            <span className={styles.compactUserName}>{user.userName || user.fullName.split(' ')[0]}</span>
          </div>
        ))}
        
        {hasMore && (
          <div className={styles.compactUserItem} onClick={onLoadMore}>
            <div className={styles.loadMoreUserItem}>
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <span>+</span>
              )}
            </div>
            <span className={styles.compactUserName}>More</span>
          </div>
        )}
      </div>
    </div>
  );
};


// Schedule Card Component - Simplified horizontal layout
const ScheduleCard = ({ schedule, isActive, onClick, onGetDirections }) => {
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return moment(timeStr, 'HH:mm').format('h:mm A');
  };

  return (
    <div 
      className={`${styles.scheduleCard} ${isActive ? styles.active : ''}`}
      onClick={() => onClick(schedule)}
    >
      <div className={styles.scheduleCardHeader}>
        <h4>{schedule.title}</h4>
        
        {isActive && (
          <div className={styles.activeBadge}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>Current</span>
          </div>
        )}
      </div>
      
      <div className={styles.scheduleCardTime}>
        <FontAwesomeIcon icon={faClock} />
        <span>{formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}</span>
        <button 
          className={styles.directionsButton}
          onClick={(e) => {
            e.stopPropagation();
            onGetDirections(schedule.fromLocation, schedule.toLocation);
          }}
        >
          <FontAwesomeIcon icon={faDirections} />
          <span>Directions</span>
        </button>
      </div>
      
      
      <div className={styles.scheduleCardRow}>
        <div className={styles.locationContainer}>
          <span className={styles.locationText}>
            <span className={styles.locationDot} style={{ backgroundColor: '#1a73e8' }}></span>
            From: {schedule.fromLocation.displayName}
          </span>
          <FontAwesomeIcon icon={faChevronRight} className={styles.locationArrow} />
          <span className={styles.locationText}>
            <span className={styles.locationDot} style={{ backgroundColor: '#1a73e8' }}></span>
            To: {schedule.toLocation.displayName}
          </span>
        </div>
      </div>
    </div>
  );
};

// Main Routes Component
const RoutesMap = () => {
  const navigate = useNavigate();
  const { selectedEvent, getSelectedDate } = useContext(TourContext);
  const location = useLocation();
  const eventType = selectedEvent?.type || 'tour';
  const { selectedDate, events, setSelectedEvent, setSelectedDate } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  // Map reference
  const mapInstance = useRef(null);
  
  // State management
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedMapDate, setSelectedMapDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // Map state
  const [center, setCenter] = useState([19.0760, 72.8777]); // Mumbai default
  const [zoom, setZoom] = useState(12);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [showVenueCard, setShowVenueCard] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  
  // Data state
  const [scheduleData, setScheduleData] = useState(null);
  const [connectionsData, setConnectionsData] = useState([]);
  const [globalData, setGlobalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  
  // Schedule data state
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleId, setActiveScheduleId] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const [showScheduleSection, setShowScheduleSection] = useState(true);
  
  // User type filter state - default to Artist
  const [selectedUserType, setSelectedUserType] = useState('Artist');
  
  // Location filter state - default to Mumbai, Maharashtra, India
  const [selectedLocation, setSelectedLocation] = useState({
    country: 'India',
    state: 'Maharashtra',
    city: 'Mumbai',
    latitude: 19.0760,
    longitude: 72.8777
  });
  
  // Theme
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  
  // Current time (for determining active schedule)
  const currentTime = useMemo(() => moment(), []);
  
  // Get allowed dates for date picker - UPDATED to use assignedUsers subcollection
  const getAllowedDates = useCallback(async () => {
    if (!selectedEvent || !selectedEvent.id) {
      console.log("No event available");
      return [];
    }
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Get user's assigned dates from the assignedUsers subcollection
      const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', userData.userId);
      const assignedUserDoc = await getDoc(assignedUserRef);
      
      if (!assignedUserDoc.exists()) {
        console.log("User not found in assignedUsers subcollection");
        return [];
      }
      
      const assignedUserData = assignedUserDoc.data();
      const assignedDates = assignedUserData.assignedDates || [];
      
      // Convert assigned dates to string format
      const dateStrings = assignedDates.map(date => {
        // Handle different possible formats
        if (typeof date === 'string') return date;
        if (date.date) return date.date;
        if (date.dateDocId) {
          // We have the dateId but need to look up the actual date
          const dateObj = selectedEvent.eventDates.find(d => d.id === date.dateDocId);
          return dateObj ? dateObj.date : null;
        }
        return null;
      }).filter(Boolean);
      
      console.log("Allowed dates for date picker:", dateStrings);
      return dateStrings;
    } catch (error) {
      console.error("Error fetching assigned dates:", error);
      return [];
    }
  }, [selectedEvent, userData.userId, eventType]);

  // Fetch schedule data (Tour Schedule tab) - UPDATED for new subcollections
  const fetchScheduleData = useCallback(async () => {
    if (!selectedEvent || !selectedDate) return;
    
    setLoading(true);
    try {
      const eventDate = getSelectedDate();
      if (!eventDate) return;
      
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const data = {
        event: null,
        hotel: null
      };
      
      // Get event venue
      if (eventDate.event?.venueDetails?.venueData) {
        const venue = eventDate.event.venueDetails.venueData;
        data.event = {
          venue: {
            ...venue,
            latitude: venue.location?.latitude || venue.latitude,
            longitude: venue.location?.longitude || venue.longitude
          },
          type: 'event'
        };
      }
      
      // Get hotel venue from hotels subcollection
      const hotelsQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'hotels')
      );
      
      const hotelsSnapshot = await getDocs(hotelsQuery);
      
      if (!hotelsSnapshot.empty) {
        const hotel = hotelsSnapshot.docs[0].data(); // Taking first hotel
        if (hotel.venueDetails && hotel.venueDetails.venueData) {
          const venueData = hotel.venueDetails.venueData;
          data.hotel = {
            venue: {
              id: venueData.id,
              displayName: venueData.displayName,
              formattedAddress: venueData.formattedAddress,
              latitude: venueData.location ? venueData.location.latitude : null,
              longitude: venueData.location ? venueData.location.longitude : null,
              photoUrl: venueData.placeCoverPhotoUrl || hotel.photoUrl
            },
            type: 'hotel'
          };
          
          console.log("Hotel data found:", data.hotel);
        }
      }
      
      setScheduleData(data);
      
      // Center map on event venue if available, otherwise on hotel
      if (data.event) {
        setCenter([data.event.venue.latitude, data.event.venue.longitude]);
      } else if (data.hotel) {
        setCenter([data.hotel.venue.latitude, data.hotel.venue.longitude]);
      }
      
      // Fetch schedule data from schedule subcollection
      const schedulesQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule'),
        orderBy('createdAt', 'desc')
      );
      
      const schedulesSnapshot = await getDocs(schedulesQuery);
      
      if (!schedulesSnapshot.empty) {
        const scheduleList = schedulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter and process schedules
        const filtered = scheduleList
          .filter(schedule => 
            schedule.fromLocation && 
            schedule.fromLocation.displayName && 
            schedule.fromLocation.formattedAddress &&
            schedule.toLocation && 
            schedule.toLocation.displayName && 
            schedule.toLocation.formattedAddress &&
            (userData.userType === 'Manager' ? true : schedule.visibility !== 'Me' || schedule.creatorId === userData.userId)
          )
          .sort((a, b) => {
            if (a.startTimeDayAfter !== b.startTimeDayAfter) {
              return a.startTimeDayAfter ? 1 : -1;
            }
            const timeA = moment(a.startTime, 'HH:mm');
            const timeB = moment(b.startTime, 'HH:mm');
            return timeA.isBefore(timeB) ? -1 : 1;
          });
        
        // Process schedules to add lat/lng if missing
        const processedSchedules = filtered.map(schedule => {
          // Ensure fromLocation has latitude/longitude
          if (schedule.fromLocation && !schedule.fromLocation.latitude && !schedule.fromLocation.longitude) {
            if (schedule.fromLocation.location) {
              schedule.fromLocation.latitude = schedule.fromLocation.location.latitude;
              schedule.fromLocation.longitude = schedule.fromLocation.location.longitude;
            }
          }
          
          // Ensure toLocation has latitude/longitude
          if (schedule.toLocation && !schedule.toLocation.latitude && !schedule.toLocation.longitude) {
            if (schedule.toLocation.location) {
              schedule.toLocation.latitude = schedule.toLocation.location.latitude;
              schedule.toLocation.longitude = schedule.toLocation.location.longitude;
            }
          }
          
          return schedule;
        });
        
        setSchedules(processedSchedules);
        
        // Set active schedule based on current time
        if (processedSchedules.length > 0) {
          const today = moment().format('YYYY-MM-DD');
          const active = processedSchedules.find(schedule => {
            const startTime = moment(`${today} ${schedule.startTime}`, 'YYYY-MM-DD HH:mm');
            const endTime = moment(`${today} ${schedule.endTime}`, 'YYYY-MM-DD HH:mm');
            
            // If current time is between start and end time
            return currentTime.isBetween(startTime, endTime);
          });
          
          // If there's an active schedule, set it
          if (active) {
            setActiveScheduleId(active.id);
            
            // Center map on active schedule's from location
            if (active.fromLocation?.latitude && active.fromLocation?.longitude) {
              setCenter([active.fromLocation.latitude, active.fromLocation.longitude]);
            }
          } else {
            // If no active schedule, get the next upcoming one
            const upcoming = processedSchedules.find(schedule => {
              const startTime = moment(`${today} ${schedule.startTime}`, 'YYYY-MM-DD HH:mm');
              return currentTime.isBefore(startTime);
            });
            
            if (upcoming) {
              setActiveScheduleId(upcoming.id);
              
              // Center map on upcoming schedule's from location
              if (upcoming.fromLocation?.latitude && upcoming.fromLocation?.longitude) {
                setCenter([upcoming.fromLocation.latitude, upcoming.fromLocation.longitude]);
              }
            }
          }
        }
      } else {
        setSchedules([]);
      }
      
      console.log("Schedule data fetched:", data);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, selectedDate, getSelectedDate, userData.userType, userData.userId, currentTime, eventType]);

  // Effects to handle initial loading and date changes
  useEffect(() => {
    if (selectedDate && selectedEvent) {
      const eventDate = getSelectedDate();
      if (eventDate) {
        // FIXED DATE HANDLING: Create date object in local timezone with noon time
        // Parse the YYYY-MM-DD format properly
        const [year, month, day] = eventDate.date.split('-').map(num => parseInt(num, 10));
        const dateObj = new Date(year, month - 1, day, 12, 0, 0); // Set to noon to avoid timezone issues
        setSelectedMapDate(dateObj);
        
        // Force reload for schedule data when date changes
        if (activeTab === 'schedule') {
          console.log("Date changed, reloading schedule data");
          fetchScheduleData();
        }
      }
    }
  }, [selectedDate, selectedEvent, getSelectedDate, fetchScheduleData, activeTab]);

  // Fetch connections data with userType filter - UPDATED for new structure
  const fetchConnectionsData = useCallback(async (date, loadMore = false) => {
    // Check if user is on free plan and this is not an assigned date
    if (userData.membershipTier === 'Free') {
      const allowedDates = await getAllowedDates();
      const dateString = date.toISOString().split('T')[0];
      
      if (!allowedDates.includes(dateString)) {
        console.log("Free user attempting to view connections outside assigned dates");
        setConnectionsData([]);
        return;
      }
    }
    
    const cacheKey = getCacheKey('connections', { 
      date: date.toISOString(), 
      userId: userData.userId,
      userType: selectedUserType 
    });
    
    if (!loadMore) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setConnectionsData(cached);
        return;
      }
    }
    
    setLoading(true);
    try {
      const db = getFirestoreInstance();
      const CHUNK_SIZE = 10;
      const BATCH_RETURN_TARGET = 30;
      
      // Get user's connections
      const connectionsRef = collection(db, 'users', userData.userId, 'connections');
      const connectionsSnapshot = await getDocs(connectionsRef);
      
      let allConnectionIds = [];
      connectionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.connectedWith && Array.isArray(data.connectedWith)) {
          allConnectionIds = [...allConnectionIds, ...data.connectedWith];
        }
      });
      
      console.log("User connections found:", allConnectionIds.length);
      
      if (allConnectionIds.length === 0) {
        setConnectionsData([]);
        setLoading(false);
        return;
      }
      
      // Chunk connections for batch queries
      const chunks = chunkArray(allConnectionIds, CHUNK_SIZE);
      const results = [];
      
      // Ensure consistent date string format with noon time to avoid timezone issues
      const dateObj = new Date(date);
      dateObj.setHours(12, 0, 0, 0); 
      const dateString = dateObj.toISOString().split('T')[0];
      
      console.log(`Searching for connections on date:`, dateString);
      
      // Start from where we left off if loading more
      const startIndex = loadMore && lastVisible ? lastVisible.chunkIndex : 0;
      
      for (let i = startIndex; i < chunks.length; i++) {
        // Updated to match actual field name in the collection
        const baseQuery = query(
          collection(db, 'user_event_dates'),
          where('userId', 'in', chunks[i]),
          where('date', '==', dateString),
          where('userType', '==', selectedUserType),
          where('mapPrivacy', 'in', ['public', 'connections'])
        );
        
        const snapshot = await getDocs(baseQuery);
        console.log(`Chunk ${i+1}/${chunks.length} returned ${snapshot.docs.length} results`);
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Ensure venue data has all properties including ID
          const venue = data.venue || {};
          
          results.push({
            id: doc.id,
            ...data,
            venue: {
              ...venue,
              // Ensure ID is preserved
              id: venue.id,
              displayName: venue.displayName || 'Unknown Venue',
              formattedAddress: venue.formattedAddress || '',
              latitude: venue.location?.latitude || venue.latitude || 0,
              longitude: venue.location?.longitude || venue.longitude || 0,
              photoUrl: venue.placeCoverPhotoUrl || ''
            }
          });
        });
        
        if (results.length >= BATCH_RETURN_TARGET) {
          setLastVisible({ chunkIndex: i + 1 });
          setHasMore(i + 1 < chunks.length);
          break;
        }
      }
      
      console.log(`Total connections found for ${dateString}: ${results.length}`);
      
      if (loadMore) {
        setConnectionsData(prev => [...prev, ...results.slice(0, BATCH_RETURN_TARGET)]);
      } else {
        setConnectionsData(results.slice(0, BATCH_RETURN_TARGET));
        setCachedData(cacheKey, results.slice(0, BATCH_RETURN_TARGET));
      }
      
      // Center map on first connection if available
      if (results.length > 0 && !loadMore) {
        setCenter([results[0].venue.latitude, results[0].venue.longitude]);
      }
    } catch (error) {
      console.error('Error fetching connections data:', error);
    } finally {
      setLoading(false);
    }
  }, [userData, lastVisible, selectedUserType, getAllowedDates]);

  // Fetch global data with userType filter - UPDATED for free user restrictions
  const fetchGlobalData = useCallback(async (date, location, loadMore = false) => {
    // Check if user is on free plan 
    if (userData.membershipTier === 'Free') {
      console.log("Free user attempting to view global data");
      setGlobalData([]);
      return;
    }
    
    if (!location || !location.city) return;
    
    const cacheKey = getCacheKey('global', { 
      date: date.toISOString(), 
      location,
      userType: selectedUserType
    });
    
    if (!loadMore) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setGlobalData(cached);
        return;
      }
    }
    
    setLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Fix for date handling - create a string in YYYY-MM-DD format with noon time
      const dateObj = new Date(date);
      dateObj.setHours(12, 0, 0, 0);
      const dateString = dateObj.toISOString().split('T')[0];
      
      console.log(`Searching for global data on ${dateString} in ${location.city}, ${location.state}, ${location.country}`);
      
      // FIXED QUERY: Use "venue.city", "venue.state", and "venue.country" instead of top-level fields
      const baseQuery = query(
        collection(db, 'user_event_dates'),
        where('date', '==', dateString),
        where('venue.country', '==', location.country.trim()), // Added trim() to handle spaces
        where('venue.state', '==', location.state),
        where('venue.city', '==', location.city),
        where('userType', '==', selectedUserType),
        where('mapPrivacy', '==', 'public'),
        orderBy('userId'),
        limit(30)
      );
      
      // Add startAfter if loading more
      let finalQuery = baseQuery;
      if (loadMore && lastVisible) {
        finalQuery = query(baseQuery, startAfter(lastVisible));
      }
      
      const snapshot = await getDocs(finalQuery);
      console.log(`Query returned ${snapshot.docs.length} global results`);
      
      const results = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Ensure venue data has all properties including ID
        const venue = data.venue || {};
        
        results.push({
          id: doc.id,
          ...data,
          venue: {
            ...venue,
            // Ensure ID is preserved
            id: venue.id,
            displayName: venue.displayName || 'Unknown Venue',
            formattedAddress: venue.formattedAddress || '',
            latitude: venue.location?.latitude || venue.latitude || 0,
            longitude: venue.location?.longitude || venue.longitude || 0,
            photoUrl: venue.placeCoverPhotoUrl || ''
          }
        });
      });
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 30);
      
      if (loadMore) {
        setGlobalData(prev => [...prev, ...results]);
      } else {
        setGlobalData(results);
        setCachedData(cacheKey, results);
      }
      
      // Center map on location
      if (!loadMore && location.latitude && location.longitude) {
        setCenter([location.latitude, location.longitude]);
        setZoom(12);
      }
    } catch (error) {
      console.error('Error fetching global data:', error);
    } finally {
      setLoading(false);
    }
  }, [lastVisible, selectedUserType, userData]);

  // Handle date selection - UPDATED for assignedUsers structure
  const handleDateSelect = async (date) => {
    // Check if free user is trying to select non-assigned date
    if (userData.membershipTier !== 'Elite' && activeTab !== 'schedule') {
      const allowedDates = await getAllowedDates();
      const dateString = date.toISOString().split('T')[0];
      
      if (!allowedDates.includes(dateString)) {
        setUpgradeFeature('Select any date');
        setShowUpgradeModal(true);
        return;
      }
    }
    
    // Update local state
    setSelectedMapDate(date);
    setShowDatePicker(false);
    
    // Find matching tour date to update TourContext
    if (selectedEvent?.eventDates) {
      // Use consistent date string format
      const selectedDateString = date.toISOString().split('T')[0];
      
      // Find the matching tour date
      const matchingeventDate = selectedEvent.eventDates.find(eventDate => {
        return eventDate.date === selectedDateString;
      });
      
      // Update TourContext selectedDate if a match is found
      if (matchingeventDate) {
        console.log("Updating TourContext selected date to:", matchingeventDate.id);
        setSelectedDate(matchingeventDate.id);
        
        // If we're in the schedule tab, also update RightMenu to show this selection
        if (activeTab === 'schedule') {
          setTimeout(() => {
            const selectedDateElement = document.querySelector(`.${styles['selected']}`);
            if (selectedDateElement) {
              selectedDateElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 100);
        }
      }
    }
    
    // Refresh data based on active tab
    if (activeTab === 'schedule') {
      fetchScheduleData();
    } else if (activeTab === 'connections') {
      fetchConnectionsData(date);
    } else if (activeTab === 'global' && selectedLocation) {
      fetchGlobalData(date, selectedLocation);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    // If free user trying to access global tab, show upgrade modal
    if (tab === 'global' && userData.membershipTier === 'Free') {
      setUpgradeFeature('Global View');
      setShowUpgradeModal(true);
      return;
    }
    
    setActiveTab(tab);
    setSelectedMarker(null);
    setShowVenueCard(false);
    
    // Load data for new tab
    if (tab === 'schedule') {
      fetchScheduleData();
    } else if (tab === 'connections') {
      fetchConnectionsData(selectedMapDate);
    } else if (tab === 'global' && selectedLocation) {
      fetchGlobalData(selectedMapDate, selectedLocation);
    }
  };

  // Load more connections
  const loadMoreConnections = () => {
    if (hasMore && !loading) {
      fetchConnectionsData(selectedMapDate, true);
    }
  };

  // Load more global data
  const loadMoreGlobal = () => {
    if (hasMore && !loading && selectedLocation) {
      fetchGlobalData(selectedMapDate, selectedLocation, true);
    }
  };

  // Handle marker click - Updated to close both cards
  const handleMarkerClick = (data) => {
    console.log("Marker clicked with data:", data);
    setSelectedMarker(data);
    setShowVenueCard(true);
    setActivePopup(data); // Track which popup is active
    
    // Center map on the marker's position
    if (data.venue) {
      setCenter([data.venue.latitude, data.venue.longitude]);
    } else if (data.latitude && data.longitude) {
      // For schedule location markers
      setCenter([data.latitude, data.longitude]);
    }
  };
  
  // Handle close venue card
  const handleCloseVenueCard = () => {
    setShowVenueCard(false);
    setSelectedMarker(null);
    setActivePopup(null);
    
    // Close any open popups on the map
    if (mapInstance.current) {
      mapInstance.current.closePopup();
    }
  };

  // Handle navigation
  const navigateToSchedule = () => {
    if (!selectedEvent?.id || !selectedDate) return;
    
    // Handle navigation to schedule correctly
    if (selectedMarker && selectedMarker.venue) {
      const eventDate = getSelectedDate();
      if (eventDate) {
        // Find the relevant schedule related to this venue - checking schedule subcollection
        navigate(`/${eventType}/${selectedEvent.id}/${selectedDate}`);
      } else {
        // If no specific schedule found, just navigate to schedule tab
        navigate(`/${eventType}/${selectedEvent.id}/${selectedDate}`);
      }
    } else {
      navigate(`/${eventType}/${selectedEvent.id}/${selectedDate}`);
    }
  };

  const navigateToVenue = (venueData) => {
    console.log("navigateToVenue called with:", venueData);
    
    if (!venueData) {
      console.error('Venue data is undefined');
      return;
    }
    
    // Extract the venue ID directly
    const venueId = venueData.id;
    
    if (venueId) {
      console.log("Navigating to venue with ID:", venueId);
      navigate(`/venue/${venueId}`);
    } else {
      // Fallback: Try to extract from Google Maps URI if ID not found
      if (venueData.googleMapsUri) {
        const cidMatch = venueData.googleMapsUri.match(/[?&]cid=([^&]+)/);
        if (cidMatch && cidMatch[1]) {
          console.log("Extracted venue ID from Google Maps URI:", cidMatch[1]);
          navigate(`/venue/${cidMatch[1]}`);
          return;
        }
      }
      
      console.error('No venue ID available for navigation', venueData);
    }
  };

  const navigateToProfile = (user) => {
    if (!user) {
      handleCloseVenueCard();
      return;
    }
    navigate(`/${user.userName}`);
  };
  
  // Handle schedule card click
  const handleScheduleCardClick = (schedule) => {
    // Set active schedule
    setActiveScheduleId(schedule.id);
    
    // Center map on schedule's from location
    if (schedule.fromLocation?.latitude && schedule.fromLocation?.longitude) {
      setCenter([schedule.fromLocation.latitude, schedule.fromLocation.longitude]);
    }
  };
  
  // Handle getting directions
  const handleGetDirections = (from, to) => {
    if (!from || !to) return;
    
    const origin = encodeURIComponent(`${from.displayName}, ${from.formattedAddress}`);
    const destination = encodeURIComponent(`${to.displayName}, ${to.formattedAddress}`);
    window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`, '_blank');
  };

  // Render schedule cards section
  const renderScheduleCardsSection = () => {
    if (!showScheduleSection || !schedules.length || activeTab !== 'schedule') return null;
    
    return (
      <div className={styles.horizontalScheduleSection}>
        <div className={styles.horizontalScheduleHeader}>
          <h3>Today's Schedule</h3>
          <button 
            className={styles.scheduleToggleButton}
            onClick={() => setShowScheduleSection(!showScheduleSection)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={styles.horizontalScheduleCards}>
          {schedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              isActive={schedule.id === activeScheduleId}
              onClick={handleScheduleCardClick}
              onGetDirections={handleGetDirections}
            />
          ))}
        </div>
      </div>
    );
  };
  
  // Render polyline between two locations
  const renderPolyline = (from, to, isActive = false) => {
    if (!from || !to || !from.latitude || !from.longitude || !to.latitude || !to.longitude) return null;
    
    const positions = [
      [from.latitude, from.longitude],
      [to.latitude, to.longitude]
    ];
    
    return (
      <Polyline
        positions={positions}
        pathOptions={{
          color: isActive ? '#E2384D' : '#1a73e8',
          weight: isActive ? 4 : 3,
          opacity: isActive ? 1 : 0.7,
          dashArray: isActive ? '' : '5, 5'
        }}
      />
    );
  };
  
  // Render map controls
  const renderMapControls = () => {
    return (
      <div className={styles.mapControls}>
        <button 
          className={`${styles.controlButton} ${!showLegend ? styles.active : ''}`}
          onClick={() => setShowLegend(!showLegend)}
          title="Toggle Legend"
        >
          <FontAwesomeIcon icon={faLayerGroup} />
        </button>
        {activeTab === 'schedule' && (
          <button 
            className={`${styles.controlButton} ${!showScheduleSection ? styles.active : ''}`}
            onClick={() => setShowScheduleSection(!showScheduleSection)}
            title="Toggle Schedule List"
          >
            <FontAwesomeIcon icon={faList} />
          </button>
        )}
      </div>
    );
  };

  // Load initial data
  useEffect(() => {
    if (activeTab === 'schedule') {
      fetchScheduleData();
    } else if (activeTab === 'connections') {
      fetchConnectionsData(selectedMapDate);
    }
  }, [activeTab, fetchScheduleData, fetchConnectionsData, selectedMapDate]);

  // Initialize with default data
  useEffect(() => {
    // Set up theme listener
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'light');
    };
    
    window.addEventListener('themechange', handleThemeChange);
    
    // If global tab is active, load the default Mumbai, Maharashtra, India data
    if (activeTab === 'global' && selectedLocation) {
      fetchGlobalData(selectedMapDate, selectedLocation);
    }
    
    // Set default date for free users to their first assigned date
    if (userData && userData.membershipTier === 'Free' && selectedEvent) {
      const fetchAllowedDates = async () => {
        const allowedDates = await getAllowedDates();
        
        if (allowedDates.length > 0) {
          // Find the matching tour date object
          const firstAssignedDate = selectedEvent.eventDates.find(
            date => date.date === allowedDates[0]
          );
          
          if (firstAssignedDate) {
            setSelectedDate(firstAssignedDate.id);
            // Create date with noon time to avoid timezone issues
            const dateObj = new Date(firstAssignedDate.date);
            dateObj.setHours(12, 0, 0, 0);
            setSelectedMapDate(dateObj);
            
            // Force data reload
            setTimeout(() => {
              if (activeTab === 'schedule') {
                fetchScheduleData();
              } else if (activeTab === 'connections') {
                fetchConnectionsData(dateObj);
              }
            }, 100);
          }
        }
      };
      
      fetchAllowedDates();
    }
    
    return () => {
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, [fetchGlobalData, activeTab, selectedLocation, userData, selectedEvent, getAllowedDates, setSelectedDate, fetchScheduleData, fetchConnectionsData]);

  return (
    <div className={styles.mainLayout}>
      <LeftMenu selectedEvent={selectedEvent} />
      
      <div className={styles.routesContainer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.tourInfo}>
            {selectedEvent && (
              <>
                <h2>{selectedEvent.tourName || selectedEvent.showName}</h2>
                <div className={styles.dateSelector} onClick={() => setShowDatePicker(true)}>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>{selectedMapDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}</span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </div>
              </>
            )}
          </div>
          
          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'schedule' ? styles.active : ''}`}
              onClick={() => handleTabChange('schedule')}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              Tour Schedule
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'connections' ? styles.active : ''}`}
              onClick={() => handleTabChange('connections')}
            >
              <FontAwesomeIcon icon={faUsers} />
              Connections
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'global' ? styles.active : ''}`}
              onClick={() => handleTabChange('global')}
            >
              <FontAwesomeIcon icon={faGlobe} />
              Global
            </button>
          </div>
        </div>

      {/* Filter Section */}
      {activeTab !== 'schedule' && (
        <div className={styles.filterSection}>
          {activeTab === 'global' && (
            <LocationFilter
              onLocationSelect={location => {
                setSelectedLocation(location);
                fetchGlobalData(selectedMapDate, location);
              }}
              disabled={loading || userData?.membershipTier === 'Free'}
            />
          )}
          
          {/* User Type Filter for both connections and global */}
          {(activeTab === 'connections' || activeTab === 'global') && (
            <UserTypeFilter
              value={selectedUserType}
              onChange={type => {
                setSelectedUserType(type);
                if (activeTab === 'connections') {
                  fetchConnectionsData(selectedMapDate);
                } else if (activeTab === 'global' && selectedLocation) {
                  fetchGlobalData(selectedMapDate, selectedLocation);
                }
              }}
              disabled={loading || (activeTab === 'global' && userData?.membershipTier === 'Free')}
            />
          )}
        </div>
      )}

      {/* Schedule Cards Section for schedule tab */}
      {activeTab === 'schedule' && renderScheduleCardsSection()}

      {/* Compact User List Above Map for Connections/Global */}
      {(activeTab === 'connections' && connectionsData.length > 0) && (
        <CompactUserList
          users={connectionsData}
          selectedUserId={selectedMarker?.id}
          onUserSelect={handleMarkerClick}
          onLoadMore={loadMoreConnections}
          hasMore={hasMore}
          loading={loading}
        />
      )}

      {(activeTab === 'global' && globalData.length > 0) && (
        <CompactUserList
          users={globalData}
          selectedUserId={selectedMarker?.id}
          onUserSelect={handleMarkerClick}
          onLoadMore={loadMoreGlobal}
          hasMore={hasMore}
          loading={loading}
        />
      )}

      {/* Map Container */}
      <div className={styles.mapContainer}>
        {renderMapControls()}
        
        <MapContainer 
          center={center} 
          zoom={zoom} 
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          ref={(map) => { mapInstance.current = map; }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url={mapThemes[theme]}
          />
          <ZoomControl position="bottomright" />
          <MapUpdater center={center} zoom={zoom} />
          
          {/* Render schedule markers for schedule tab */}
          {activeTab === 'schedule' && schedules.map((schedule) => {
            const isActive = schedule.id === activeScheduleId;
            return (
              <React.Fragment key={schedule.id}>
                {schedule.fromLocation && schedule.fromLocation.latitude && schedule.fromLocation.longitude && 
                  <ScheduleMarker 
                    location={schedule.fromLocation} 
                    type="from" 
                    onClick={handleMarkerClick} 
                    isActive={isActive} 
                  />
                }
                {schedule.toLocation && schedule.toLocation.latitude && schedule.toLocation.longitude && 
                  <ScheduleMarker 
                    location={schedule.toLocation} 
                    type="to" 
                    onClick={handleMarkerClick} 
                    isActive={isActive} 
                  />
                }
                {renderPolyline(schedule.fromLocation, schedule.toLocation, isActive)}
              </React.Fragment>
            );
          })}
          
          {/* Render event venue and hotel markers */}
          {activeTab === 'schedule' && scheduleData?.event && scheduleData.event.venue && (
            <VenueMarker 
              venue={scheduleData.event.venue} 
              type="event" 
              onClick={handleMarkerClick} 
            />
          )}
          
          {activeTab === 'schedule' && scheduleData?.hotel && scheduleData.hotel.venue && (
            <VenueMarker 
              venue={scheduleData.hotel.venue} 
              type="hotel" 
              onClick={handleMarkerClick} 
            />
          )}
          
          {/* Render connection/global user markers */}
          {activeTab === 'connections' && connectionsData.map(connection => (
            <UserMarker
              key={connection.id}
              user={connection}
              onClick={handleMarkerClick}
              isSelected={selectedMarker?.id === connection.id}
            />
          ))}
          
          {activeTab === 'global' && globalData.map(user => (
            <UserMarker
              key={user.id}
              user={user}
              onClick={handleMarkerClick}
              isSelected={selectedMarker?.id === user.id}
            />
          ))}
          
          {/* Map Legend */}
          <MapLegend 
            showLegend={showLegend} 
            setShowLegend={setShowLegend} 
            showSchedule={activeTab === 'schedule'}
          />
        </MapContainer>

        {/* Loading Overlay */}
        {loading && (
          <div className={styles.loadingOverlay}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {/* Venue Card */}
        {showVenueCard && selectedMarker && (
          <VenueCard
            venue={selectedMarker.venue || selectedMarker}
            users={activeTab === 'connections' || activeTab === 'global' ? [selectedMarker] : null}
            onViewSchedule={activeTab === 'schedule' ? navigateToSchedule : null}
            onViewVenue={navigateToVenue}
            onViewProfile={navigateToProfile}
            onClose={handleCloseVenueCard}
          />
        )}
        
        {/* Empty State for Global */}
        {activeTab === 'global' && !selectedLocation && userData?.membershipTier !== 'Free' && (
          <div className={styles.emptyStateOverlay}>
            <div className={styles.emptyStateContent}>
              <FontAwesomeIcon icon={faGlobe} size="3x" />
              <h3>Select a Location</h3>
              <p>Choose a location from the filters above to see users in that area.</p>
            </div>
          </div>
        )}
        
        {/* Empty State for Schedule */}
        {activeTab === 'schedule' && (!scheduleData || (!scheduleData.event && !scheduleData.hotel) && schedules.length === 0) && !loading && (
          <NoDataMessage activeTab="schedule" userData={userData} />
        )}
        
        {/* Empty State for Connections */}
        {activeTab === 'connections' && connectionsData.length === 0 && !loading && (
          <NoDataMessage activeTab="connections" userData={userData} />
        )}
        
        {/* Empty State for Global */}
        {activeTab === 'global' && globalData.length === 0 && !loading && selectedLocation && (
          <NoDataMessage activeTab="global" userData={userData} />
        )}
      </div>
       
      <RightMenu
        selectedEvent={selectedEvent}
        events={events}
        onEventSelect={setSelectedEvent}
      />

      {/* Modals */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelect}
        selectedDate={selectedMapDate}
        allowedDates={[]} // Will be filled by getAllowedDates when needed
        isElite={userData?.membershipTier === 'Elite'}
        selectedEvent={selectedEvent}
        activeTab={activeTab}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
    </div>
    </div>
  );
};

export default RoutesMap;